import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { cn } from '@/lib/utils';
import {
    segmentedControlButtonVariants,
    segmentedControlListVariants,
    segmentedControlTrackVariants,
} from './segmented-control-styles';
import { SegmentedCountBadge } from './SegmentedControl';

type SegmentedTabItem = {
    value: string;
    label: React.ReactNode;
    content: React.ReactNode;
    count?: number;
    disabled?: boolean;
};

type SegmentedTabsProps = Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    'children' | 'defaultValue'
> & {
    tabs: SegmentedTabItem[];
    defaultValue?: string;
    headerAction?: React.ReactNode;
    headerClassName?: string;
    actionClassName?: string;
    listClassName?: string;
    triggerClassName?: string;
    contentClassName?: string;
};

const SegmentedTabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    SegmentedTabsProps
>(
    (
        {
            tabs,
            defaultValue,
            value,
            className,
            headerAction,
            headerClassName,
            actionClassName,
            listClassName,
            triggerClassName,
            contentClassName,
            ...props
        },
        ref,
    ) => {
        if (tabs.length === 0) return null;

        return (
            <TabsPrimitive.Root
                ref={ref}
                value={value}
                defaultValue={value === undefined ? (defaultValue ?? tabs[0].value) : undefined}
                className={cn('space-y-6', className)}
                {...props}
            >
                <div
                    className={cn(
                        'flex w-full items-center justify-between gap-3',
                        headerClassName,
                    )}
                >
                    <div
                        className={cn(
                            segmentedControlListVariants({ variant: 'tabs' }),
                            listClassName,
                        )}
                    >
                        <TabsPrimitive.List
                            className={segmentedControlTrackVariants({ variant: 'tabs' })}
                        >
                            {tabs.map((tab) => (
                                <TabsPrimitive.Trigger
                                    key={tab.value}
                                    value={tab.value}
                                    disabled={tab.disabled}
                                    className={cn(
                                        segmentedControlButtonVariants({
                                            variant: 'tabs',
                                            active: false,
                                        }),
                                        'data-[state=active]:border-brand/25 data-[state=active]:bg-brand-subtle data-[state=active]:text-brand data-[state=active]:shadow-sm [&[data-state=active]_[data-slot=segmented-count]]:bg-card [&[data-state=active]_[data-slot=segmented-count]]:text-brand',
                                        triggerClassName,
                                    )}
                                >
                                    <span>{tab.label}</span>
                                    {typeof tab.count === 'number' && (
                                        <SegmentedCountBadge count={tab.count} active={false} />
                                    )}
                                </TabsPrimitive.Trigger>
                            ))}
                        </TabsPrimitive.List>
                    </div>

                    {headerAction ? (
                        <div className={cn('shrink-0 pb-1', actionClassName)}>{headerAction}</div>
                    ) : null}
                </div>

                {tabs.map((tab) => (
                    <TabsPrimitive.Content
                        key={tab.value}
                        value={tab.value}
                        className={cn(
                            'mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:data-[state=active]:duration-200 motion-safe:data-[state=active]:animate-in motion-safe:data-[state=active]:fade-in-0 motion-safe:data-[state=active]:slide-in-from-bottom-2',
                            contentClassName,
                        )}
                    >
                        {tab.content}
                    </TabsPrimitive.Content>
                ))}
            </TabsPrimitive.Root>
        );
    },
);

SegmentedTabs.displayName = 'SegmentedTabs';

export default SegmentedTabs;
